import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { DataTable as PrimeDataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import { Paginator } from "primereact/paginator";

import useFetchData from "hooks/useFetchDataWithDep";
import Translate from "Components/Translate";

import { REFRESH, THERE_IS_NO_DATA_FOUND } from "Constant";

import { useSelector } from "react-redux";
import { toggleRefresh } from "../../redux/searchLink";
import { store } from "../../redux/store";

import CreateForfaitBtn from "./CreateForfaitBtn";

const DEFAULT_SORTING = [{ id: "created_at", desc: true }];

const RefreshBar = ({ profileCode ,canCreateFlatfee }) => (
  <div className="flex flex-wrap gap-2">
    {canCreateFlatfee && (
    <CreateForfaitBtn profileCode={profileCode} />
    )}
    <Button
      onClick={() => store.dispatch(toggleRefresh())}
      label={<Translate>{REFRESH}</Translate>}
      icon="pi pi-sync"
      severity="secondary"
    />
  </div>
);

export default function ForfaitsDataTable({ newColumns, link ,profileCode ,canCreateFlatfee }) {
  const { refresh } = useSelector((state) => state.searchLink);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(globalFilter);

  const { t } = useTranslation();

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.append("size", pagination.pageSize);
    params.append("index", pagination.pageIndex + 1);

    if (debouncedSearch) {
      params.append("search", debouncedSearch);
    }

    sorting.forEach((filter) => {
      const val = filter.desc ? "DESC" : "ASC";
      params.append("sort_order", val);
      params.append("sort_column", filter.id);
    });

    let queryString = params.toString();
    queryString = queryString
      .replaceAll(/%20/g, " ")
      .replaceAll(/%3A/g, ":")
      .replaceAll("+", " ")
      .replaceAll("undefined", "");

    return `${link}?${queryString}`;
  }, [
    pagination.pageSize,
    pagination.pageIndex,
    debouncedSearch,
    sorting,
    link,
  ]);

  const { data, isLoading, isError } = useFetchData(url, refresh, url);
  const { total: paginationTotalRows, rows: listOfData } = getRows(data);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(globalFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [globalFilter]);

  const onPageChange = (e) => {
    setPagination({
      pageIndex: e.page,
      pageSize: e.rows,
    });
  };

  return (
    <Fragment>
      <Toolbar
        style={{ padding: "0.5srem 1rem", fontSize: "0.75rem" }}
        start={<RefreshBar profileCode={profileCode} canCreateFlatfee={canCreateFlatfee} />}
      />
      <PrimeDataTable
        loading={isLoading}
        value={listOfData}
        emptyMessage={t(THERE_IS_NO_DATA_FOUND)}
        showGridlines
        paginator={false}
        columnResizeMode="fit"
        stripedRows
        resizableColumns
        rowHover
        size="small"
      >
        {newColumns.map((col, i) => {
          if (col.Cell) {
            // Column with custom Cell renderer (Actions column)
            return (
              <Column
                key={i}
                header={t(col.header || col.name)}
                body={(rowData) => col.Cell({ row: { original: rowData } })}
              />
            );
          }
          return (
            <Column
              key={i}
              body={col.body ? col.body : null}
              field={col.selector ? col.selector : null}
              header={t(col.name)}
            />
          );
        })}
      </PrimeDataTable>
      <div className="card">
        <Paginator
          first={pagination.pageIndex * pagination.pageSize}
          rows={pagination.pageSize}
          totalRecords={paginationTotalRows}
          rowsPerPageOptions={rowsPerPageOptions(paginationTotalRows)}
          onPageChange={onPageChange}
          dropdownAppendTo={document.body}
          template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={` {first} - {last} | {totalRecords}`}
        />
      </div>
    </Fragment>
  );
}

const getRows = (data) => {
  try {
    return data.data.results.data;
  } catch {
    return { total: 1, rows: [] };
  }
};

const rowsPerPageOptions = (total) => {
  const options = [5, 10, 50, 100, 500, 1000];
  if (!options.includes(total)) {
    options.push(total);
  }
  return options.filter((option) => option <= total).sort((a, b) => a - b);
};
